import { useState, useEffect, useRef } from 'react';
import { Play, Pause, ChevronLeft, ChevronRight, X, Loader2, Music } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

/**
 * Floating Audio Player — Chaptered Guide
 * Supports chapter-by-chapter navigation with auto-advance and preloading.
 */
export const AudioPlayer = ({
  chapters = [],
  currentChapterIndex = 0,
  loadingChapterIndex = null,
  title,
  onChapterChange,
  onClose,
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isAudioLoading, setIsAudioLoading] = useState(false);

  const currentChapter = chapters[currentChapterIndex];
  const audioUrl = currentChapter?.audio_url;
  const isCurrentChapterLoading = loadingChapterIndex === currentChapterIndex;

  // Re-initialise audio when chapter changes (audioUrl changes)
  useEffect(() => {
    // Cleanup previous audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    if (!audioUrl) return;

    setIsAudioLoading(true);
    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
      setIsAudioLoading(false);
    });

    audio.addEventListener('timeupdate', () => {
      setCurrentTime(audio.currentTime);
    });

    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      setCurrentTime(0);
      // Auto-advance to next chapter after 800ms pause
      if (currentChapterIndex < chapters.length - 1) {
        setTimeout(() => onChapterChange?.(currentChapterIndex + 1), 800);
      }
    });

    audio.addEventListener('canplaythrough', () => setIsAudioLoading(false));
    audio.addEventListener('error', () => setIsAudioLoading(false));

    // Auto-play new chapter
    audio.play().then(() => setIsPlaying(true)).catch(() => setIsAudioLoading(false));

    return () => {
      audio.pause();
      audio.src = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const handleSeek = (value) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = value[0];
    setCurrentTime(value[0]);
  };

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    onClose?.();
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const canGoBack = currentChapterIndex > 0;
  const canGoForward = currentChapterIndex < chapters.length - 1;
  const isLoading = isAudioLoading || isCurrentChapterLoading;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-[400px]"
      data-testid="audio-player"
    >
      <div className="bg-gradient-to-br from-[#1A1F2E] to-[#2D3348] rounded-2xl shadow-2xl p-4 border border-white/10 backdrop-blur-sm">

        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-purple-500 to-pink-500">
              {isLoading
                ? <Loader2 className="w-5 h-5 text-white animate-spin" />
                : <Music className="w-5 h-5 text-white" />
              }
            </div>
            <div className="min-w-0">
              <p className="text-white text-sm font-semibold truncate">{title || 'Guide Audio'}</p>
              <p className="text-purple-300 text-xs truncate">
                {currentChapter?.title || `Chapitre ${currentChapterIndex + 1}`}
              </p>
            </div>
          </div>
          <Button
            variant="ghost" size="sm"
            onClick={handleClose}
            className="h-8 w-8 p-0 text-white/50 hover:text-white hover:bg-white/10 flex-shrink-0"
            data-testid="audio-player-close"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Chapter info row */}
        <div className="flex items-center justify-between mb-2 px-0.5">
          <span className="text-[11px] text-white/50 font-medium truncate max-w-[200px]">
            {isCurrentChapterLoading ? 'Chargement...' : (currentChapter?.title || '')}
          </span>
          <span className="text-[11px] text-white/35 flex-shrink-0 ml-2">
            {currentChapterIndex + 1} / {chapters.length}
          </span>
        </div>

        {/* Progress / Seek */}
        <div className="mb-3">
          <Slider
            value={[currentTime]}
            max={duration || 100}
            step={0.1}
            onValueChange={handleSeek}
            disabled={isLoading || !audioUrl}
            className="cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-white/40 mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="ghost" size="sm"
            onClick={() => onChapterChange?.(currentChapterIndex - 1)}
            disabled={!canGoBack || isLoading}
            className="h-9 w-9 p-0 text-white/70 hover:text-white hover:bg-white/10 rounded-full disabled:opacity-25"
            data-testid="audio-prev-chapter"
            title="Chapitre précédent"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>

          <Button
            onClick={togglePlay}
            disabled={isLoading || !audioUrl}
            className={`h-12 w-12 p-0 rounded-full shadow-lg transition-all active:scale-95 ${
              isPlaying
                ? 'bg-rose-500 hover:bg-rose-600 shadow-rose-500/30'
                : 'bg-[#3A86FF] hover:bg-[#2563EB] shadow-blue-500/30'
            }`}
            data-testid="audio-play-pause"
          >
            {isLoading
              ? <Loader2 className="w-5 h-5 animate-spin" />
              : isPlaying
              ? <Pause className="w-5 h-5" />
              : <Play className="w-5 h-5 ml-0.5" />
            }
          </Button>

          <Button
            variant="ghost" size="sm"
            onClick={() => onChapterChange?.(currentChapterIndex + 1)}
            disabled={!canGoForward || isLoading}
            className="h-9 w-9 p-0 text-white/70 hover:text-white hover:bg-white/10 rounded-full disabled:opacity-25"
            data-testid="audio-next-chapter"
            title="Chapitre suivant"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Chapter dot indicators */}
        {chapters.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-3">
            {chapters.map((ch, i) => (
              <button
                key={i}
                onClick={() => onChapterChange?.(i)}
                className={`rounded-full transition-all duration-200 ${
                  i === currentChapterIndex
                    ? 'w-6 h-1.5 bg-purple-400'
                    : ch.audio_url
                    ? 'w-1.5 h-1.5 bg-white/30 hover:bg-white/50'
                    : 'w-1.5 h-1.5 bg-white/15 hover:bg-white/30'
                }`}
                title={ch.title || `Chapitre ${i + 1}`}
                data-testid={`chapter-dot-${i}`}
              />
            ))}
          </div>
        )}

        {/* Immersive audio badge */}
        <div className="flex items-center justify-center gap-2 mt-3">
          {[...Array(7)].map((_, i) => (
            <div
              key={i}
              className="w-0.5 bg-gradient-to-t from-purple-500 to-pink-400 rounded-full"
              style={{
                height: isPlaying ? `${6 + Math.sin(i * 1.2) * 5}px` : '4px',
                animationDelay: `${i * 0.1}s`,
                transition: 'height 0.3s ease',
              }}
            />
          ))}
          <span className="text-[10px] text-purple-300/70 mx-1">Google Chirp HD</span>
          {[...Array(7)].map((_, i) => (
            <div
              key={`r${i}`}
              className="w-0.5 bg-gradient-to-t from-pink-500 to-purple-400 rounded-full"
              style={{
                height: isPlaying ? `${6 + Math.cos(i * 1.1) * 5}px` : '4px',
                transition: 'height 0.3s ease',
              }}
            />
          ))}
        </div>

      </div>
    </div>
  );
};

export default AudioPlayer;
